import React from "react";
import { Link, useLocation } from "react-router-dom";
import FancyTitle from "@/components/FancyTitle";
import { AIRBNB_URL, MANOR_NAME } from "@/lib/config";

export default function Navigation() {
  const { pathname } = useLocation();
  const nav = [
    { to: "/house", label: "Home" },
    { to: "/music-room", label: "Music Room", stack: ["Music", "Room"] },
    { to: "/garden", label: "Garden" },
    { to: "/house#kit", label: "Kit" },
    { to: "/gallery", label: "Gallery" },
    { to: "/diary", label: "Diary" },
    { to: "/house#pricing", label: "Rates" },
  ];

  const renderNav = (extraClass = "") => (
    <nav className={`flex items-center ${extraClass}`}>
      {nav.map((n) => {
        const active = pathname === n.to;
        return (
          <Link
            key={n.to}
            to={n.to}
            className={`font-sans text-[0.66rem] lg:text-[0.7rem] uppercase tracking-[0.18em] lg:tracking-[0.16em] xl:tracking-[0.2em] transition-colors whitespace-nowrap ${
              active ? "text-burgundy" : "text-ink/70 hover:text-ink"
            }`}
            data-testid={`nav-${n.label.toLowerCase()}-link`}
          >
            {n.stack ? (
              <span className="flex flex-col items-center leading-[1.05]">
                {n.stack.map((w) => (
                  <span key={w}>{w}</span>
                ))}
              </span>
            ) : (
              n.label
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <header
      className="sticky top-0 z-40 bg-parchment/95 backdrop-blur-sm border-b border-ink/15"
      data-testid="site-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-3">
        <Link
          to="/"
          className="inline-flex items-center gap-2 font-sans text-[0.64rem] uppercase tracking-[0.24em] text-ink/55 hover:text-burgundy"
          data-testid="nav-invitations-link"
        >
          ← Invitations
        </Link>
      </div>
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-3 md:py-4 flex items-center justify-between gap-4">
        <Link
          to="/house"
          className="flex items-center gap-2 group shrink-0"
          data-testid="nav-home-link"
        >
          <FancyTitle as="span" className="press-title text-sm md:text-[0.95rem] text-ink leading-none uppercase tracking-[0.28em] md:tracking-[0.36em] whitespace-nowrap">
            The Magicians' House
          </FancyTitle>
        </Link>

        {/* Desktop inline nav */}
        {renderNav("hidden lg:flex gap-6 xl:gap-7")}

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <a
            href={AIRBNB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex text-[0.68rem] md:text-[0.72rem] font-sans uppercase tracking-[0.2em] md:tracking-[0.22em] border border-ink px-3 md:px-4 py-2 hover:bg-ink hover:text-parchment transition-colors"
            data-testid="nav-airbnb-link"
          >
            Airbnb
          </a>
          <Link
            to="/enquire"
            className="text-[0.68rem] md:text-[0.72rem] font-sans uppercase tracking-[0.2em] md:tracking-[0.22em] bg-burgundy text-parchment px-3 md:px-4 py-2 hover:bg-ink transition-colors"
            data-testid="nav-enquire-cta"
          >
            Enquire
          </Link>
        </div>
      </div>

      {/* Mobile / tablet nav — wraps to two rows if needed */}
      <div className="lg:hidden border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-2">
          {renderNav("flex-wrap justify-center gap-x-4 sm:gap-x-5 gap-y-2")}
        </div>
      </div>
    </header>
  );
}
